/**
 * Resolve a simulation `transferPortId` to the equipment that owns it and to a
 * local load-port index, so a PICKUP / DROP only animates the one tool and the
 * one dock the OHT is actually working, instead of every tool standing near the
 * rail. Mirrors what the stocker already does with `resolveKitPortForTransfer`.
 */
import type { LayoutElement } from '../../../../types';
import type { EquipmentInstance } from '../../../../features/equipment/equipmentTypes';
import { alignEquipmentPortsToLoadDocks, portEdge, type Port } from '../../../../utils/ports';

/** Port ids are minted as `EQ-1-LP-2` — the dock number is 1-based. */
const PORT_ID_SUFFIX = /^(.*)-LP-(\d+)$/;

export const canonicalEquipmentId = (value: string): string =>
  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

export type ElementPortOwnership = {
  /** Every id the simulation might use to refer to this element. */
  ownerIds: Set<string>;
  /** Load-port id → local dock index, left→right. */
  portIndexById: Map<string, number>;
};

export type EquipmentPortDirectory = {
  /** Load-port id → owning element / instance id, across layout, overlay and sim. */
  ownerByPortId: Map<string, string>;
};

export type TransferPortMatch = {
  /**
   * `mine` — this element owns the port; `other` — a different tool owns it;
   * `unknown` — nothing in the layout claims the port, so the caller may fall
   * back to proximity.
   */
  owner: 'mine' | 'other' | 'unknown';
  /** Local dock index when it could be resolved. */
  portIndex: number | null;
};

/** Parent equipment id encoded by a standard `EQ-N-LP-M` port id. */
export function equipmentIdFromPortId(portId: string | undefined | null): string | null {
  if (!portId) return null;
  return PORT_ID_SUFFIX.exec(portId)?.[1] ?? null;
}

/**
 * Match backend and layout aliases such as `EQ-2` and `EQ2`.
 * IDs remain scoped to one equipment; this is not a proximity fallback.
 */
export function ownershipMatchesEquipmentId(
  ownership: ElementPortOwnership,
  candidate: string | undefined | null
): boolean {
  if (!candidate) return false;
  if (ownership.ownerIds.has(candidate)) return true;
  const canonical = canonicalEquipmentId(candidate);
  return [...ownership.ownerIds].some(
    (ownerId) => canonicalEquipmentId(ownerId) === canonical
  );
}

function idsOf(element: LayoutElement): Set<string> {
  return new Set(
    [
      element.id,
      element.metadata.name,
      String(element.customProperties?.loadPortId ?? ''),
      String(element.customProperties?.amhsId ?? ''),
      String(element.customProperties?.dxfEquipmentId ?? ''),
      String(element.customProperties?.toolId ?? ''),
    ].filter((v) => v.length > 0)
  );
}

/** Docks left→right, matching the pose order in `getProcessEquipmentPortPoses`. */
function sortDocks(ports: readonly Port[]): Port[] {
  return [...ports].sort((a, b) => {
    const ax = typeof a.rx === 'number' ? a.rx : 0.5;
    const bx = typeof b.rx === 'number' ? b.rx : 0.5;
    if (Math.abs(ax - bx) > 1e-6) return ax - bx;
    return String(a.id).localeCompare(String(b.id));
  });
}

/**
 * Along-dock fractions for the 3D kit, taken from the same 2D ports the
 * markers and OHT use (`rx` on a top/bottom face, `ry` on a side face).
 *
 * The result is expressed in the kit's own local +X, not in layout space. A
 * kit facing `top` or `right` is rendered rotated by 180°/90°, which flips its
 * local +X against the axis `rx`/`ry` is measured on, so those two faces are
 * mirrored here. Without this the seats land on the opposite side of the
 * cabinet from their node and the OHT drops onto the wrong dock.
 */
export function dockFractionsAlongFace(
  ports: readonly Port[],
  count: number
): number[] | undefined {
  if (ports.length === 0 || count <= 0) return undefined;
  const sorted = sortDocks(ports);
  const edge = portEdge(sorted[0]!);
  const mirrored = edge === 'top' || edge === 'right';
  const along = (port: Port) => {
    const fraction =
      edge === 'left' || edge === 'right' ? (port.ry ?? 0.5) : (port.rx ?? 0.5);
    return mirrored ? 1 - fraction : fraction;
  };
  return Array.from({ length: count }, (_, i) =>
    along(sorted[i] ?? sorted[sorted.length - 1]!)
  );
}

export function portsOf(
  element: LayoutElement,
  instances: Record<string, EquipmentInstance> | undefined
): Port[] {
  const own = element.customProperties?.ports;
  // Both sources bypass `normalizePorts`, so the legacy default pair is spaced
  // out here too. Without it the 3D seats sit 0.05 either side of centre while
  // 2D and the backend use the remapped anchors.
  if (Array.isArray(own) && own.length > 0) {
    return sortDocks(alignEquipmentPortsToLoadDocks([...(own as Port[])], 1, 1));
  }
  if (!instances) return [];
  // Overlay-backed equipment carries only the instance id; the docks live on the
  // instance, not on the synthesised layout element.
  for (const candidate of idsOf(element)) {
    const ports = instances[candidate]?.overrides?.ports;
    if (Array.isArray(ports) && ports.length > 0) {
      return sortDocks(alignEquipmentPortsToLoadDocks([...ports], 1, 1));
    }
  }
  return [];
}

/** Widest dock bank the procedural kit can lay out. */
const MAX_KIT_LOAD_PORTS = 6;

/**
 * How many docks this tool really has, from the same port list 2D draws.
 * Hard-coding two docks in 3D silently folded `…-LP-3` / `…-LP-4` transfers onto
 * dock 2, so a four-port tool animated the wrong shutter.
 */
export function resolveElementLoadPortCount(
  element: LayoutElement | null | undefined,
  instances?: Record<string, EquipmentInstance>,
  fallback = 2
): number {
  if (!element) return fallback;
  const count = portsOf(element, instances).length;
  if (count <= 0) return fallback;
  return Math.min(MAX_KIT_LOAD_PORTS, count);
}

/**
 * Dock count and along-face fractions for one tool.
 *
 * The full kit and the instanced overview silhouette both need this pair, and
 * both must agree: resolving it twice by hand is how a dock ends up in a
 * different place depending on which detail level drew the tool.
 */
export function resolveKitDockLayout(
  element: LayoutElement | null | undefined,
  instances?: Record<string, EquipmentInstance>
): { loadPortCount: number; portFractions: number[] | undefined } {
  const loadPortCount = resolveElementLoadPortCount(element, instances);
  return {
    loadPortCount,
    portFractions: element
      ? dockFractionsAlongFace(portsOf(element, instances), loadPortCount)
      : undefined,
  };
}

export function buildElementPortOwnership(
  element: LayoutElement | null | undefined,
  instances?: Record<string, EquipmentInstance>
): ElementPortOwnership {
  if (!element) return { ownerIds: new Set(), portIndexById: new Map() };
  const portIndexById = new Map<string, number>();
  portsOf(element, instances).forEach((port, index) => {
    if (typeof port?.id === 'string' && port.id) portIndexById.set(port.id, index);
  });
  return { ownerIds: idsOf(element), portIndexById };
}

/**
 * Every port the app can attribute to an owner: layout element docks, overlay
 * instance docks, and the port→owner maps the AMHS response ships.
 */
export function buildEquipmentPortDirectory(
  elements: Record<string, LayoutElement> | undefined,
  instances: Record<string, EquipmentInstance> | undefined,
  portOwners: Array<Record<string, string> | undefined>
): EquipmentPortDirectory {
  const ownerByPortId = new Map<string, string>();
  for (const element of Object.values(elements ?? {})) {
    const ports = element.customProperties?.ports;
    if (!Array.isArray(ports)) continue;
    for (const port of ports as Port[]) {
      if (typeof port?.id === 'string' && port.id) ownerByPortId.set(port.id, element.id);
    }
  }
  for (const instance of Object.values(instances ?? {})) {
    for (const port of instance.overrides?.ports ?? []) {
      if (typeof port?.id === 'string' && port.id) ownerByPortId.set(port.id, instance.id);
    }
  }
  for (const map of portOwners) {
    for (const [portId, ownerId] of Object.entries(map ?? {})) {
      if (portId && ownerId) ownerByPortId.set(portId, ownerId);
    }
  }
  return { ownerByPortId };
}

/** Dock index encoded in an `…-LP-N` id, or `null` when the id is free-form. */
function indexFromPortId(portId: string, portCount: number): number | null {
  const match = PORT_ID_SUFFIX.exec(portId);
  if (!match) return null;
  const dock = Number(match[2]);
  if (!Number.isFinite(dock) || dock < 1) return null;
  return Math.min(dock - 1, Math.max(0, portCount - 1));
}

/**
 * Dock index for a port the caller has already attributed to this element —
 * exact layout id first, then the `…-LP-N` suffix, because the backend does not
 * always echo the layout's port ids verbatim.
 */
export function resolveOwnedPortIndex(
  ownership: ElementPortOwnership,
  transferPortId: string | undefined | null,
  portCount: number
): number | null {
  if (!transferPortId) return null;
  const exact = ownership.portIndexById.get(transferPortId);
  if (exact !== undefined) return Math.min(exact, Math.max(0, portCount - 1));
  return indexFromPortId(transferPortId, portCount);
}

export function matchTransferPort(
  directory: EquipmentPortDirectory,
  ownership: ElementPortOwnership,
  transferPortId: string | undefined | null,
  portCount: number
): TransferPortMatch {
  if (!transferPortId) return { owner: 'unknown', portIndex: null };

  const localIndex = ownership.portIndexById.get(transferPortId);
  if (localIndex !== undefined) {
    return { owner: 'mine', portIndex: Math.min(localIndex, Math.max(0, portCount - 1)) };
  }

  // Overlay elements are keyed by label and carry no dock list, so also accept
  // the port id itself and the `EQ-1` parent encoded in `EQ-1-LP-2`.
  const parentId = equipmentIdFromPortId(transferPortId);
  const owner = directory.ownerByPortId.get(transferPortId);
  const mine =
    (owner !== undefined && ownershipMatchesEquipmentId(ownership, owner)) ||
    ownershipMatchesEquipmentId(ownership, transferPortId) ||
    ownershipMatchesEquipmentId(ownership, parentId);

  if (mine) return { owner: 'mine', portIndex: indexFromPortId(transferPortId, portCount) };
  if (owner !== undefined) return { owner: 'other', portIndex: null };
  return { owner: 'unknown', portIndex: null };
}
